import { MouseEvent, useMemo, useCallback, useContext, useRef } from 'react';

import * as Dfns from 'date-fns/fp';

import { MdDelete, MdArrowForward, MdAdd, MdWarning, MdContentCopy } from 'react-icons/md';

import { ListGroup, Popover, Tooltip, OverlayTrigger } from 'react-bootstrap';

import cn from 'classnames';

import { IconButton } from '../../Components/IconButton';
import { Design, Intent, Placement, Size } from '../../Components/designConstants';

import { Neutral400, getColorMap, getContrastTextColor } from '../../utils/colors';
import { useStateFunction } from '../../utils/hooks/useData';
import { useScrollbarSizes } from '../../utils/hooks/useDOMHelpers';
import { useSetAreYouSure } from '../../redux/modals';

import { FilterBar } from '../../Components/FilterBar';

import {
  OrderViewContext,
  toPrettyDate,
  subFlightsToPropertyList,
  toPrettySpendWithCents,
  toPrettyImpressions
} from './order-view-utils';
import { Flight, FlightChange, Order } from './streaming-buying-types';

import './OrderView.scss';
import { useExperimentFlag } from '../../utils/experiment-context';

const DATE_FORMAT = 'yyyy-MM-dd';

const FILTER_BAR_OPTIONS = [
  {
    name: 'id',
    label: 'ID'
  },
  {
    name: 'startDate',
    label: 'Start Date'
  },
  {
    name: 'endDate',
    label: 'End Date'
  },
  {
    name: 'platform',
    label: 'Platform'
  },
  {
    name: 'description',
    label: 'Description'
  },
  {
    name: 'spend',
    label: 'Spend'
  },
  {
    name: 'cpm',
    label: 'CPM'
  },
  {
    name: 'impressions',
    label: 'Impressions'
  },
  {
    name: 'dsp',
    label: 'DSP'
  },
  {
    name: 'isNew',
    label: 'Is New'
  },
  {
    name: 'canceled',
    label: 'Canceled'
  },
  {
    name: 'changed',
    label: 'Changed'
  }
];

const TODAY = Dfns.format(DATE_FORMAT, new Date());

interface FlightDetailsProps {
  order: Order;
  flight: Flight;
  derivedIDColor: string;
  enableStreamingBuyingUpdates?: boolean;
}

const FlightDetails = ({
  order,
  flight,
  derivedIDColor,
  enableStreamingBuyingUpdates
}: FlightDetailsProps) => {
  const setAreYouSure = useSetAreYouSure();

  const enableBeeswaxIntegration = useExperimentFlag('enableBeeswaxIntegration');
  const beeswaxIntegrationFlipDate = useExperimentFlag('beeswaxIntegrationFlipDate');
  const placementSpecsExperiment = useExperimentFlag('enablePlacementSpecs');
  const enablePlacementSpecs = order.placementSpecsExperiment ?? placementSpecsExperiment;

  const {
    id,
    isNew,
    platform,
    description,
    content,
    additional,
    startDate,
    endDate,
    dsp,
    spotPosition,
    showTitle,
    created,
    useDescription
  } = flight;
  const {
    flightChangeMap,
    selectedFlight,
    setSelectedFlight,
    isMultiProperty,
    saveFlightChange,
    setCopiedFlight,
    setSameTab,
    setShowCopiedToast
  } = useContext(OrderViewContext);
  const flightChanges = useMemo<FlightChange>(
    () => flightChangeMap[id] || {},
    [flightChangeMap, id]
  );
  const flightHasChanges = useMemo(
    () => Boolean(Object.keys(flightChanges).length),
    [flightChanges]
  );

  const canceled = useMemo(
    () => (flightChanges.canceled == null ? flight.canceled : flightChanges.canceled),
    [flight, flightChanges]
  );

  let variant: 'dark' | 'primary' | undefined;
  if (endDate < TODAY) {
    variant = 'dark';
  } else if (startDate <= TODAY) {
    variant = 'primary';
  }

  const makeEditedItem = useCallback(
    <K extends keyof Flight>(
      key: K,
      transform: (value: Flight[K]) => string,
      className: string
    ) => {
      const hasChange = !(isNew || flightChanges[key] == null);
      const itemContent = transform(flight[key]);
      const changeContent = hasChange ? transform(flightChanges[key] as Flight[K]) : undefined;

      return (
        <div className={className}>
          <div className={hasChange ? 'strikethrough' : ''} title={itemContent}>
            {itemContent}
          </div>
          {hasChange && (
            <div className='change' title={changeContent}>
              {changeContent}
            </div>
          )}
        </div>
      );
    },
    [isNew, flightChanges, flight]
  );

  const isSelected = selectedFlight === id;
  const details = useMemo(() => {
    let concatenatedDescription: string;
    if (enableStreamingBuyingUpdates && !useDescription) {
      concatenatedDescription = content
        ? `${content}${additional ? ` - ${additional}` : ''}`
        : `${showTitle} | ${spotPosition}${additional ? ` | ${additional}` : ''}`;
    } else {
      concatenatedDescription = showTitle
        ? `${showTitle} | ${spotPosition}${description ? ` | ${description}` : ''}`
        : `${description}`;
    }
    return `${platform}${dsp ? ` | ${dsp}` : ''}${
      concatenatedDescription ? ` | ${concatenatedDescription}` : ''
    }`;
  }, [
    enableStreamingBuyingUpdates,
    useDescription,
    platform,
    content,
    showTitle,
    additional,
    spotPosition,
    dsp,
    description
  ]);

  const onDiscard = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        await setAreYouSure({
          title: 'Discard Changes?',
          message: 'Are you sure you want to discard all changes to this flight?',
          cancelText: 'Never mind',
          okayText: "Yes, I'm sure"
        });
      } catch {
        return;
      }
      saveFlightChange(id);
    },
    [setAreYouSure, saveFlightChange, id]
  );

  const derivedPill = useMemo(() => {
    const backgroundColor = derivedIDColor === 'new' ? Neutral400 : derivedIDColor;

    const pill = (
      <small
        className='derived-id'
        style={{ backgroundColor, color: getContrastTextColor(backgroundColor) }}
      >
        {flight.derivedNetwork || 'NEW FLIGHT'}
      </small>
    );

    if (derivedIDColor === 'new') {
      return (
        <OverlayTrigger
          overlay={
            <Popover style={{ pointerEvents: 'none' }}>
              <Popover.Body>
                This is a new flight. It won&apos;t get a derived ID until the order is saved.
              </Popover.Body>
            </Popover>
          }
          placement='top'
        >
          {pill}
        </OverlayTrigger>
      );
    }

    return pill;
  }, [derivedIDColor, flight]);

  const copyFlight = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const copiedFlight = {
        copiedPlatform: flight.platform,
        copiedDescription: flight.description,
        copiedDSP: flight.dsp,
        copiedSpotPosition: flight.spotPosition,
        copiedShowTitle: flight.showTitle,
        copiedCPM: flight.cpm,
        copiedImpressions: flight.impressions,
        copiedAudioBuyType: flight.audioBuyType,
        copiedSpend: flight.spend,
        copiedNumOfSpots: flight.numOfSpots,
        copiedSpotRate: flight.spotRate,
        copiedGeo: flight.geo
      };

      setSameTab(true);
      setCopiedFlight(copiedFlight);
      setShowCopiedToast(true);
    },
    [flight, setCopiedFlight, setSameTab, setShowCopiedToast]
  );

  return (
    <ListGroup.Item
      as='div'
      action
      active={isSelected}
      data-testid={`flight-row-${id}`}
      className={cn('flight-row', { 'is-new': isNew, canceled }, variant)}
      onClick={() => setSelectedFlight(isSelected ? null : id)}
      variant={variant}
    >
      <div className='id'>
        {id.toString().length === 6 ? id : 'New'}
        {enableBeeswaxIntegration &&
          dsp === 'Beeswax' &&
          created &&
          beeswaxIntegrationFlipDate > created && (
            <OverlayTrigger
              placement='right-start'
              overlay={
                <Tooltip>
                  This flight was created before Beeswax automation was turned on. You will still
                  need to maintain changes manually in Beeswax.
                </Tooltip>
              }
            >
              <MdWarning color='orange' className='alert-icon' />
            </OverlayTrigger>
          )}
      </div>
      <div className='description' title={details}>
        {details}
        {derivedPill}
      </div>
      {isMultiProperty && makeEditedItem('subFlights', subFlightsToPropertyList, 'properties')}
      <div className='date-box'>
        {makeEditedItem('startDate', toPrettyDate, 'start')}
        <MdArrowForward className='arrow' />
        {makeEditedItem('endDate', toPrettyDate, 'end')}
      </div>
      {makeEditedItem('spend', toPrettySpendWithCents, 'spend')}
      {makeEditedItem('impressions', toPrettyImpressions, 'impressions')}
      {makeEditedItem('cpm', toPrettySpendWithCents, 'cpm')}
      <div className='actions'>
        {!isNew && enablePlacementSpecs && (
          <IconButton
            size={Size.MD}
            design={Design.NEUTRAL}
            onClick={copyFlight}
            tooltip={{ text: 'Copy Flight', placement: Placement.LEFT }}
          >
            <MdContentCopy />
          </IconButton>
        )}
        {flightHasChanges && (
          <IconButton
            size={Size.MD}
            design={Intent.ERROR}
            onClick={onDiscard}
            tooltip={{ text: 'Discard Changes', placement: Placement.LEFT }}
          >
            <MdDelete />
          </IconButton>
        )}
      </div>
    </ListGroup.Item>
  );
};

export const FlightList = () => {
  const {
    flightChangeMap,
    order,
    isMultiProperty,
    selectedFlight,
    setSelectedFlight,
    networkInfo
  } = useContext(OrderViewContext);

  const listRef = useRef<HTMLDivElement | null>(null);
  const { width: scrollbarWidth } = useScrollbarSizes(listRef);
  type LineFilter = (line: Record<string, unknown>) => boolean;
  const [filter, setFilter] = useStateFunction<LineFilter>(() => true);
  const enableStreamingBuyingUpdates = networkInfo?.enableTaxonomyUpdates;

  const orderedFlights = useMemo<Flight[]>(() => {
    const { flights } = order;
    const newFlights = Object.values(flightChangeMap).filter(c => c.isNew) as Flight[];
    const combined = [...newFlights, ...flights];
    return combined.sort((a, b) => {
      const startDiff = (b.startDate ?? '').localeCompare(a.startDate ?? '');
      if (startDiff) return startDiff;
      const endDiff = (b.endDate ?? '').localeCompare(a.endDate ?? '');
      if (endDiff) return endDiff;
      const descDiff = (a.description ?? '').localeCompare(b.description ?? '');
      if (descDiff) return descDiff;
      const platDiff = (a.platform ?? '').localeCompare(b.platform ?? '');
      if (platDiff) return platDiff;
      return String(b.id ?? '').localeCompare(String(a.id ?? ''));
    });
  }, [order, flightChangeMap]);

  const filterBarLines = useMemo(
    () =>
      orderedFlights.map(({ id, isNew, canceled, subFlights, ...rest }) => ({
        ...rest,
        isNew: Boolean(isNew),
        id: isNew ? 'new' : id,
        canceled: canceled || flightChangeMap[id]?.canceled ? 'canceled' : 'false',
        changed: flightChangeMap[id] ? 'changed' : 'false',
        properties: subFlightsToPropertyList(subFlights)
      })),
    [orderedFlights, flightChangeMap]
  );

  const filteredFlights = useMemo(
    () =>
      orderedFlights.filter(
        flight =>
          filter({
            ...flight,
            isNew: Boolean(flight.isNew),
            id: flight.isNew ? 'new' : flight.id,
            canceled:
              flight.canceled || flightChangeMap[flight.id]?.canceled ? 'canceled' : 'false',
            changed: flightChangeMap[flight.id] ? 'changed' : 'false',
            properties: subFlightsToPropertyList(flight.subFlights)
          }) || filter(flightChangeMap[flight.id] || {})
      ),
    [filter, orderedFlights, flightChangeMap]
  );

  const filterBarOptions = [...FILTER_BAR_OPTIONS];
  if (isMultiProperty) {
    filterBarOptions.push({
      name: 'properties',
      label: 'Properties'
    });
  }

  const totals = useMemo(() => {
    let minStart: string | undefined;
    let maxEnd: string | undefined;
    let spend = 0;
    let impressions = 0;
    for (const flight of filteredFlights) {
      const changes = flightChangeMap[flight.id] || {};
      const start = changes.startDate || flight.startDate;
      if (!minStart || start < minStart) {
        minStart = start;
      }
      const end = changes.endDate || flight.endDate;
      if (!maxEnd || end > maxEnd) {
        maxEnd = end;
      }
      spend += changes.spend ?? flight.spend;
      impressions += changes.impressions ?? flight.impressions;
    }
    return { start: minStart, end: maxEnd, spend, impressions };
  }, [filteredFlights, flightChangeMap]);

  const derivedColorMap = useMemo(() => {
    const derivedIDs = [
      ...new Set(orderedFlights.map(f => f.derivedNetwork).filter((d): d is string => Boolean(d)))
    ];
    return getColorMap(derivedIDs.map(name => ({ name })));
  }, [orderedFlights]);

  return (
    <>
      <div className='flight-list-filter-bar'>
        <FilterBar
          size={Size.LG}
          onFilter={f => setFilter(f as LineFilter)}
          lines={filterBarLines}
          options={filterBarOptions}
        />
      </div>
      <div className='flight-list-scroll-box' ref={listRef}>
        <div className='flight-list-box'>
          <ListGroup variant='flush' className='list-header'>
            <ListGroup.Item className='flight-row' style={{ marginRight: scrollbarWidth }}>
              <div className='id'>Flight ID</div>
              <div className='description'>Details</div>
              {isMultiProperty && <div className='properties'>Properties</div>}
              <div className='date-box'>Dates</div>
              <div className='spend'>Spend</div>
              <div className='impressions'>Impressions</div>
              <div className='cpm'>CPM</div>
              <div className='actions'>
                <IconButton
                  size={Size.MD}
                  disabled={Boolean(selectedFlight)}
                  onClick={() => setSelectedFlight('new')}
                >
                  <MdAdd />
                </IconButton>
              </div>
            </ListGroup.Item>
          </ListGroup>
          <ListGroup variant='flush' className='flight-list' data-testid='flight-list'>
            {filteredFlights.map(flight => (
              <FlightDetails
                key={flight.id}
                order={order}
                flight={flight}
                derivedIDColor={
                  (flight.derivedNetwork && derivedColorMap[flight.derivedNetwork]) || 'new'
                }
                enableStreamingBuyingUpdates={enableStreamingBuyingUpdates}
              />
            ))}
          </ListGroup>
          <ListGroup variant='flush' className='list-totals' data-testid='flight-totals'>
            <ListGroup.Item className='flight-row' style={{ marginRight: scrollbarWidth }}>
              <div className='id'>Totals</div>
              <div className='description' />
              {isMultiProperty && <div className='properties' />}
              <div className='date-box'>
                <div className='start'>{totals.start && toPrettyDate(totals.start)}</div>
                <MdArrowForward className='arrow' />
                <div className='end'>{totals.end && toPrettyDate(totals.end)}</div>
              </div>
              <div className='spend'>{toPrettySpendWithCents(totals.spend)}</div>
              <div className='impressions'>{toPrettyImpressions(totals.impressions)}</div>
              <div className='cpm' />
              <div className='actions' />
            </ListGroup.Item>
          </ListGroup>
        </div>
      </div>
    </>
  );
};
